// The rule card that opens a themed round: "Round 2 of 6", the round's name and its rule in one line. Shown on phones
// during the pre-roll of a round's first question, in place of the usual intro card; the bar fills until the question
// opens, timed from the server's startsAt (round openers get a longer pre-roll than other questions). Each thing is said
// once: the round count as words (no pips beside it), the time left as the bar (as seconds only when motion is reduced).
// Each round has its own accent and icon from the ward palette. Reduced motion: everything fades in, nothing travels.
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { BellRing, ListOrdered, Search, Stethoscope, TestTube, Zap, type LucideIcon } from 'lucide-react';
import { ROUND_INFO, type RoundKey, type RoundTag } from '@shared/types';
import { Pulse, type PulseMood } from '@/brand/Pulse';
import { StaggerWords } from '@/motion';
import { useReducedMotion } from '@/motion/reduced';
import { serverNow } from '@/lib/api';

interface RoundLook { Icon: LucideIcon; band: string; badge: string; pip: string; mood: PulseMood }
/** Accent per round: the band behind the Pulse, the icon badge and the countdown bar (pip). Text always stays navy on white. */
export const ROUND_LOOK: Record<RoundKey, RoundLook> = {
  'ward-round': { Icon: Stethoscope, band: 'bg-brand-tint text-brand', badge: 'bg-brand-strong text-white', pip: 'bg-brand', mood: 'steady' },
  bloods: { Icon: TestTube, band: 'bg-paper text-flag-h', badge: 'bg-paper text-flag-h ring-1 ring-paper-edge', pip: 'bg-flag-h', mood: 'steady' },
  bleep: { Icon: BellRing, band: 'bg-iv-soft text-iv', badge: 'bg-iv text-white', pip: 'bg-iv', mood: 'racing' },
  diagnose: { Icon: Search, band: 'bg-lime-soft text-ink-2', badge: 'bg-lime text-ink', pip: 'bg-ink', mood: 'steady' },
  'order-pick': { Icon: ListOrdered, band: 'bg-[var(--mark-bolt-soft)] text-[var(--mark-bolt-deep)]', badge: 'bg-mark-bolt text-ink', pip: 'bg-mark-bolt', mood: 'steady' },
  'crash-call': { Icon: Zap, band: 'bg-cart-soft text-cart', badge: 'bg-cart text-ink', pip: 'bg-cart', mood: 'racing' },
};

export const roundOf = (i: number, total: number) => `Round ${i} of ${total}`;
export const startingIn = (seconds: number) => `Starting in ${seconds}`;

/** Whole seconds until a server moment, ticking once a second (the card's countdown). */
function useSecondsLeft(at: number | undefined): number | undefined {
  const calc = () => (typeof at === 'number' ? Math.max(0, Math.ceil((at - serverNow()) / 1000)) : undefined);
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    if (typeof at !== 'number') return;
    let t: number | undefined;
    const loop = () => { const ms = at - serverNow(); setLeft(Math.max(0, Math.ceil(ms / 1000))); if (ms > 0) t = window.setTimeout(loop, (ms % 1000 || 1000) + 10); };
    loop();
    return () => { if (t) window.clearTimeout(t); };
  }, [at]);
  return left;
}

/** The round's name and rule, from the item's tag, else from ROUND_INFO. */
export function roundText(round: Pick<RoundTag, 'key' | 'name' | 'rule'>): { name: string; rule: string } {
  const info = ROUND_INFO[round.key];
  const name = typeof round.name === 'string' && round.name.trim() ? round.name.trim() : info?.name ?? 'Next round';
  const rule = typeof round.rule === 'string' && round.rule.trim() ? round.rule.trim() : info?.rule ?? '';
  return { name, rule };
}

export interface RoundCardProps {
  round: RoundTag;
  /** Server time at which the round's first question opens: the bar fills until then. */
  startsAt?: number;
  className?: string;
}

export function RoundCard({ round, startsAt, className = '' }: RoundCardProps) {
  const reduce = useReducedMotion();
  const look = ROUND_LOOK[round.key] ?? ROUND_LOOK['ward-round'];
  const { name, rule } = roundText(round);
  const total = Math.max(1, Math.floor(round.total || 1));
  const index = Math.min(total, Math.max(1, Math.floor(round.index || 1)));
  const label = roundOf(index, total);
  // Timed from the server's moment, measured once when the card appears, so a re-render never restarts the bar.
  const [remaining] = useState(() => (typeof startsAt === 'number' ? Math.max(0, startsAt - serverNow()) : 0));
  const left = useSecondsLeft(startsAt);
  const rise = (delay: number) => (reduce ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.25, delay } }
    : { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { type: 'spring' as const, stiffness: 380, damping: 26, delay } });

  return (
    <motion.section aria-label={`${label}: ${name}`} data-round={round.key} data-testid="round-card"
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 56, scale: 0.92, rotate: 1.5 }} animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
      transition={reduce ? { duration: 0.3 } : { type: 'spring', stiffness: 250, damping: 21 }}
      className={`flex flex-col overflow-hidden rounded-card bg-surface shadow-pop ${className}`}>
      <div className={`flex items-center gap-3 px-6 pb-2 pt-4 ${look.band}`}>
        <div className="min-w-0 flex-1"><Pulse mood={look.mood} width="100%" height={34} /></div>
        <motion.span aria-hidden="true" className={`grid size-12 shrink-0 place-items-center rounded-tile shadow-lift ${look.badge}`}
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.3, rotate: -24 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={reduce ? { duration: 0.25, delay: 0.1 } : { type: 'spring', stiffness: 520, damping: 15, delay: 0.28 }}>
          <look.Icon className="size-6" strokeWidth={2.4} />
        </motion.span>
      </div>

      <div className="flex flex-col gap-3 px-6 pb-6 pt-4">
        <motion.p className="font-semibold text-ink-2" {...rise(0.1)}>{label}</motion.p>
        <h1 className="font-display text-[2.5rem] font-extrabold leading-[1.02] tracking-tight text-ink">
          {reduce ? name : <StaggerWords text={name} delayMs={200} stepMs={75} />}
        </h1>
        {rule && <motion.p className="text-lg leading-snug text-ink-2" {...rise(0.55)}>{rule}</motion.p>}
        {/* Time until the question opens, said once: a bar that fills (linear, it is time). Reduced motion swaps it for
            "Starting in 5", since a bar that cannot move would sit full and say nothing. */}
        <div className="mt-1 flex min-h-6 items-center gap-3" aria-hidden="true">
          {reduce && left !== undefined && left > 0 && (
            <span data-testid="round-card-seconds" className="shrink-0 text-sm font-semibold text-ink-2">{startingIn(left)}</span>
          )}
          {!reduce && (
            <div className="h-1.5 flex-1 overflow-hidden bg-surface-2">
              <motion.div data-testid="round-card-bar" data-ms={Math.round(remaining)} className={`h-full origin-left ${look.pip}`}
                initial={{ scaleX: remaining > 0 ? 0 : 1 }} animate={{ scaleX: 1 }} transition={{ duration: remaining / 1000, ease: 'linear' }} />
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
}
